/**
 * Where every card, pill and wire sits on the board (ACL-338).
 *
 * Pure: a `Row[]` from `layout()` goes in, absolute coordinates come out. No React, no DOM, no
 * measuring — so the whole board can be tested by assertion rather than by eye, which is the only
 * way to catch two columns quietly overlapping.
 *
 * **Two passes, widths up then positions down.** Pass one measures each subtree bottom-up, so a
 * split knows how wide its columns are before anything is placed. Pass two spends that width
 * top-down from the centre. The ordering is the whole trick: a branch that splits again widens its
 * own column during the measure, so its siblings are already pushed apart by the time they are
 * placed, and a nested split needs no special case.
 *
 * **An empty branch still takes a card of space.** A path with no steps yet — which is every path
 * the moment "make a path per answer" creates them — gets a ghost card to land on. Drawing nothing
 * there is what made four new paths look like no paths at all.
 */

import type { Branch, Row } from "./intake-flow";

export const METRICS: Record<
  "cardW" | "cardH" | "pillH" | "pillBlock" | "gapX" | "gapY" | "busDrop" | "pad",
  number
> = {
  cardW: 268,
  cardH: 76,
  pillH: 28,
  /** Pill, the gap under it, and the drop line above it. */
  pillBlock: 72,
  gapX: 32,
  gapY: 56,
  busDrop: 28,
  pad: 64,
};

export interface PlacedNode {
  kind: "node" | "ghost" | "ref";
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  depth: number;
  /** For a ghost: the branch it belongs to, so the plus knows where to insert. */
  from?: string;
  position?: number;
}

export interface PlacedPill {
  key: string;
  from: string;
  position: number;
  x: number;
  y: number;
  w: number;
  h: number;
  fallback: boolean;
  label: string | null;
}

export interface PlacedEdge {
  kind: "trunk" | "bus" | "drop" | "join";
  points: { x: number; y: number }[];
  /**
   * The branch this line draws, as the graph names it: the step it leaves and that branch's
   * `position`. It is what lets the live canvas ask "was this one walked?" and draw it green
   * (ACL-338).
   *
   * `position` rather than the step it leads to, because that is what identifies the line: two
   * of a split's branches may lead to the same place without being the same branch. Absent on a
   * `bus`, which is the horizontal rail across a split's columns and belongs to no one branch —
   * each column's own `drop` carries the identity instead.
   */
  from?: string;
  position?: number;
}

export interface Board {
  nodes: PlacedNode[];
  pills: PlacedPill[];
  edges: PlacedEdge[];
  bounds: { x: number; y: number; w: number; h: number };
}

interface Cell {
  row: Row;
  w: number;
  h: number;
  columns?: { span: Span; branch: Branch }[];
}

interface Span {
  w: number;
  h: number;
  cells: Cell[];
}

/**
 * How tall particular cards need to be, by node key.
 *
 * The board reserves one card's worth of room per step, which is right for a card that shows a
 * title and a line of text. The live canvas puts a control in one — the question being answered,
 * or the firm a split goes by — and that card is two or three times as tall. Without saying so
 * here it was drawn over the top of whatever came next: the cards paint in order, so the one
 * below covered the taller one's own buttons, and "Set the firm" was behind the next card.
 *
 * A hint rather than a measurement, because the geometry is pure and runs before anything is in
 * the DOM: the caller knows which card holds a control and roughly how much it needs.
 */
export type Tall = ReadonlyMap<string, number> | undefined;

function cardHeight(key: string, tall: Tall): number {
  return Math.max(METRICS.cardH, tall?.get(key) ?? 0);
}

/** Pass one: how much room each subtree needs, bottom-up. */
function measure(rows: Row[], collapsed: ReadonlySet<string>, tall: Tall): Span {
  const cells: Cell[] = [];
  let height = 0;
  let width = METRICS.cardW;

  for (const row of rows) {
    const own = cardHeight(row.key, tall);
    if (row.kind === "step" || !row.branches?.length) {
      cells.push({ row, w: METRICS.cardW, h: own });
      height += own;
      continue;
    }
    const columns = row.branches.map((branch) => {
      // A collapsed column, and an empty one, both reserve a single card: an empty path is a
      // ghost the admin builds on, never a gap.
      const span =
        collapsed.has(`${row.key}:${branch.position}`) || branch.rows.length === 0
          ? { w: METRICS.cardW, h: METRICS.cardH, cells: [] }
          : measure(branch.rows, collapsed, tall);
      return { span, branch };
    });
    const columnsWidth =
      columns.reduce((sum, c) => sum + c.span.w, 0) + METRICS.gapX * (columns.length - 1);
    const tallest = Math.max(...columns.map((c) => c.span.h));
    const w = Math.max(METRICS.cardW, columnsWidth);
    const h = own + METRICS.busDrop + METRICS.pillBlock + tallest;
    cells.push({ row, w, h, columns });
    height += h;
    width = Math.max(width, w);
  }

  height += METRICS.gapY * Math.max(0, cells.length - 1);
  width = Math.max(width, ...cells.map((c) => c.w));
  return { w: width, h: height, cells };
}

/** Pass two: spend the measured width from the centre, top-down. */
function place(
  span: Span,
  centerX: number,
  top: number,
  depth: number,
  out: { nodes: PlacedNode[]; pills: PlacedPill[]; edges: PlacedEdge[] },
  above: PlacedNode | null,
  tall: Tall,
): PlacedNode | null {
  let y = top;
  let previous = above;
  // The branch the trunk below `previous` draws, which only `previous`'s own row knows.
  let leaving: number | undefined;

  for (const cell of span.cells) {
    const x = centerX - METRICS.cardW / 2;
    const node: PlacedNode = {
      kind: "node",
      key: cell.row.key,
      x,
      y,
      w: METRICS.cardW,
      h: cardHeight(cell.row.key, tall),
      depth,
    };
    out.nodes.push(node);
    if (previous) out.edges.push(trunk(previous, node, leaving));
    previous = node;
    leaving = cell.row.out;

    if (cell.columns?.length) {
      const busY = y + node.h + METRICS.busDrop / 2;
      const pillY = y + node.h + METRICS.busDrop;
      const bodyTop = pillY + METRICS.pillBlock;
      const total =
        cell.columns.reduce((sum, c) => sum + c.span.w, 0) +
        METRICS.gapX * (cell.columns.length - 1);
      let cursor = centerX - total / 2;
      const centres: number[] = [];

      for (const column of cell.columns) {
        const colCx = cursor + column.span.w / 2;
        centres.push(colCx);
        const branch = column.branch;
        out.pills.push({
          key: `${cell.row.key}:${branch.position}`,
          from: cell.row.key,
          position: branch.position,
          x: colCx - METRICS.cardW / 2,
          y: pillY,
          w: METRICS.cardW,
          h: METRICS.pillH,
          fallback: (branch.conditions?.length ?? 0) === 0,
          label: branch.label,
        });
        out.edges.push({
          kind: "drop",
          from: cell.row.key,
          position: branch.position,
          points: [
            { x: colCx, y: busY },
            { x: colCx, y: pillY },
          ],
        });
        if (column.span.cells.length === 0) {
          out.nodes.push({
            kind: "ghost",
            key: `${cell.row.key}:${branch.position}:ghost`,
            x: colCx - METRICS.cardW / 2,
            y: bodyTop,
            w: METRICS.cardW,
            h: METRICS.cardH,
            depth: depth + 1,
            from: cell.row.key,
            position: branch.position,
          });
        } else {
          place(column.span, colCx, bodyTop, depth + 1, out, null, tall);
        }
        cursor += column.span.w + METRICS.gapX;
      }

      // One bus across every column, dropped from the split card.
      const left = Math.min(centerX, ...centres);
      const right = Math.max(centerX, ...centres);
      out.edges.push({
        kind: "bus",
        points: [
          { x: centerX, y: y + node.h },
          { x: centerX, y: busY },
          { x: left, y: busY },
          { x: right, y: busY },
        ],
      });
      // The merge card is the next cell in this chain; the trunk line from the split would cut
      // through the columns, so the split stops being the previous node.
      previous = null;
    }

    y += cell.h + METRICS.gapY;
  }
  return previous;
}

function trunk(from: PlacedNode, to: PlacedNode, position?: number): PlacedEdge {
  const fromX = from.x + from.w / 2;
  const toX = to.x + to.w / 2;
  const fromY = from.y + from.h;
  const toY = to.y;
  const named = { from: from.key, position };
  if (Math.abs(fromX - toX) < 0.5) {
    return {
      kind: "trunk",
      ...named,
      points: [{ x: fromX, y: fromY }, { x: toX, y: toY }],
    };
  }
  const mid = (fromY + toY) / 2;
  return {
    kind: "trunk",
    ...named,
    points: [
      { x: fromX, y: fromY },
      { x: fromX, y: mid },
      { x: toX, y: mid },
      { x: toX, y: toY },
    ],
  };
}

/**
 * An SVG path through these points, with the corners rounded.
 *
 * A corner rounds by at most half of the shorter leg, so a tight dogleg bends less rather than
 * overshooting into a cusp.
 */
export function path(points: { x: number; y: number }[], radius = 10): string {
  if (points.length < 2) return "";
  const parts = [`M ${points[0]!.x} ${points[0]!.y}`];
  for (let n = 1; n < points.length - 1; n += 1) {
    const before = points[n - 1]!;
    const at = points[n]!;
    const after = points[n + 1]!;
    const inLen = Math.hypot(at.x - before.x, at.y - before.y);
    const outLen = Math.hypot(after.x - at.x, after.y - at.y);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    if (r < 0.5) {
      parts.push(`L ${at.x} ${at.y}`);
      continue;
    }
    const inX = at.x - ((at.x - before.x) / (inLen || 1)) * r;
    const inY = at.y - ((at.y - before.y) / (inLen || 1)) * r;
    const outX = at.x + ((after.x - at.x) / (outLen || 1)) * r;
    const outY = at.y + ((after.y - at.y) / (outLen || 1)) * r;
    parts.push(`L ${inX} ${inY}`, `Q ${at.x} ${at.y} ${outX} ${outY}`);
  }
  const last = points[points.length - 1]!;
  parts.push(`L ${last.x} ${last.y}`);
  return parts.join(" ");
}

/** The whole board: every card, pill and wire, with the box that holds them. */
export function buildBoard(
  rows: Row[],
  collapsed: ReadonlySet<string> = new Set(),
  tall?: Tall,
): Board {
  const out = { nodes: [] as PlacedNode[], pills: [] as PlacedPill[], edges: [] as PlacedEdge[] };
  const span = measure(rows, collapsed, tall);
  place(span, 0, 0, 0, out, null, tall);

  const xs = [
    ...out.nodes.flatMap((n) => [n.x, n.x + n.w]),
    ...out.pills.flatMap((p) => [p.x, p.x + p.w]),
    ...out.edges.flatMap((e) => e.points.map((p) => p.x)),
  ];
  const ys = [
    ...out.nodes.flatMap((n) => [n.y, n.y + n.h]),
    ...out.pills.flatMap((p) => [p.y, p.y + p.h]),
    ...out.edges.flatMap((e) => e.points.map((p) => p.y)),
  ];
  // An empty flow has no points at all; Math.min of nothing is Infinity.
  const minX = xs.length ? Math.min(...xs) : 0;
  const maxX = xs.length ? Math.max(...xs) : 0;
  const minY = ys.length ? Math.min(...ys) : 0;
  const maxY = ys.length ? Math.max(...ys) : 0;

  return {
    ...out,
    bounds: {
      x: minX - METRICS.pad,
      y: minY - METRICS.pad,
      w: maxX - minX + METRICS.pad * 2,
      h: maxY - minY + METRICS.pad * 2,
    },
  };
}
